import cn from 'classnames';
import styles from './CompareCard.module.css';
import { PropertyData } from '../CardsComponents/types';
import { calculateDiscountedPrice } from '../../utils/discountPriceUtils';

interface CompareCardProps {
  data: PropertyData;
  isFirst: boolean;
  isLast: boolean;
  isOne: boolean;
  onClick: () => void;
}

export default function CompareCard({ data, isFirst, isLast, isOne, onClick }: CompareCardProps) {
  const parsePrice = (price: string | null | undefined): number => {
    if (!price || typeof price !== 'string') {
      return 0;
    }
    return Number(price.replace(/\s/g, '')) || 0;
  };

  const basePrice = parsePrice(data.price);

  const discountProperty = data.discountProperty ?? 0;
  const discountFull = data.discountFull ?? 0;

  const discountedPrice = data.price && data.discountProperty
    ? calculateDiscountedPrice(
        data.pricePerM2 ?? '0',
        discountProperty,
        data.price
      )
    : basePrice;

  const computedPrice = parsePrice(String(discountedPrice));

  const fullPaymentAmount = computedPrice - discountFull;

  const isDiscounted = discountProperty > 0 || discountFull > 0;

  const displayedPrice = isDiscounted ? fullPaymentAmount : basePrice;

  const installmentPrice = basePrice;
  const installmentMonths = 50;
  const downPayment = Math.round(installmentPrice * 0.3);
  const monthlyPayment = Math.round((installmentPrice - downPayment) / installmentMonths);

  const objectType = data.type || 'flat';
  const isFlat = objectType === 'flat';

  const formatArea = (value: string | null | undefined) => {
    if (!value) return '';
    const numericValue = Number(value.replace(',', '.').replace(/\s/g, ''));
    if (Number.isNaN(numericValue)) {
      return value;
    }
    return numericValue.toLocaleString('ru-RU', { minimumFractionDigits: 1, maximumFractionDigits: 1 });
  };
  
  const getObjectTitle = () => {
    switch (objectType) {
      case 'business':
        return `Помещение ${data.area || ''} м²`;
      case 'parking':
        return `Машино-место ${data.area || ''} м²`;
      default:
        return `${('roomType' in data ? data.roomType : '') || ''} от ${formatArea(data.area)} м²`;
    }
  };

  const getLocationInfo = () => {
    switch (objectType) {
      case 'business':
      case 'parking':
        const floor = data.floor;
        const floorText = floor === 0 ? 'Цоколь' : `${floor} этаж`;
        const numberPlace = 'numberPlace' in data ? data.numberPlace : '';
        return `№${numberPlace || ''}, ${floorText}`;
      default:
        const entrance = 'entrance' in data ? data.entrance : '';
        const totalEntrances = 'totalEntrances' in data ? data.totalEntrances : '';
        return `${entrance || ''}/${totalEntrances || ''} подъезд, ${data.floor || ''}/${data.totalFloors || ''} этаж`;
    }
  };

  const shouldShowLivingArea = objectType === 'flat';

  return (
    <div
      className={cn(styles.wrapper, {
        [styles.rounded_first_wrapper]: isFirst,
        [styles.rounded_last_wrapper]: isLast,
        [styles.rounded_middle_wrapper]: !isFirst && !isLast,
        [styles.rounded_wrapper]: isOne,
      })}
    >
      <div className={styles.image_container}>
        <img src={('image' in data ? data.image : '') || ''} alt='Изображение объекта' />
        <span className={styles.close_button} onClick={onClick}></span>
      </div>
      <div className={styles.info}>
        <p className={cn('text_captions_c2', styles.status)}>статус</p>
        <p className={cn('text_subheaders_sh2', styles.header)}>
          {getObjectTitle()}
        </p>
        <p className={cn('text_body_b3', styles.text)}>
          {getLocationInfo()}
        </p>
        <p className={cn('text_body_b3', styles.text)}>Заселение {data.checkIn}</p>
      </div>
      <div
        className={cn('text_body_b3', styles.more_info_table, {
          [styles.rounded_first]: isFirst,
          [styles.rounded_last]: isLast,
          [styles.rounded_one]: isOne
        })}
      >
        <div className={cn(styles.more_info, styles.first_payment)}>
          <p className={styles.text}>Первый взнос</p>
          <p className={styles.black_text}>от {downPayment.toLocaleString()} ₽</p>
        </div>
        <div className={cn(styles.more_info, styles.loan)}>
          <p className={styles.text}>Рассрочка</p>
          <p className={styles.black_text}>от {monthlyPayment.toLocaleString()} ₽ / месяц</p>
        </div>
        <div className={cn(styles.more_info, styles.total_area)}>
          <p className={styles.text}>Общая площадь</p>
          <p className={styles.black_text}>{isFlat ? `от ${formatArea(data.area)} м²` : `${data.area || ''} м²`}</p>
        </div>
        {shouldShowLivingArea && (
          <div className={cn(styles.more_info, styles.area)}>
            <p className={styles.text}>Жилая площадь</p>
            <p className={styles.black_text}>от {('livingArea' in data ? data.livingArea : '') || ''} м²</p>
          </div>
        )}
      </div>
      <div className={styles.button_wrapper}>
        <button className={cn('text_subheaders_sh2', styles.price_button)}>
          от {displayedPrice.toLocaleString()} ₽
        </button>
      </div>
    </div>
  );
}
